import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ds } from '@/components/lib/design.system';
import { useDeposit } from '@/feature/deposit/hooks/use-deposit';
import { useActiveProductName } from '@/hooks/use-active-product-name';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { DepositStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { getWalletErrorCode } from '@/utils/get-wallet-error-code';

import { TrustlyStatusMessage } from '../components/TrustlyStatusMessage';
import { PaymentMethodTypes } from '../const';
import { useDepositStatusStore } from '../hooks/use-deposit-status-store';
import { useTrustlyHandleAvailableFundsGuidance } from '../hooks/use-trustly-handle-available-funds-guidance';
import { useTrustlyStatus } from '../hooks/use-trustly-status';
import { TrustlyStatus, TrustlyStatusValue } from '../utils/trustly-status';

const styles = StyleSheet.create({
    loaderContainer: { backgroundColor: ds.colorValue.background.neutral.base },
});

type ScreenProps = NativeStackScreenProps<DepositStackParamList, 'TrustlyDepositScreen'>;

export const TrustlyDepositScreen = ({ route }: ScreenProps) => {
    const { currency = 'USD', payment_type_id } = route.params || {};
    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const coinPackage = useDepositStatusStore(state => state.coinPackage);
    const navigation = useNavigation<NativeStackNavigationProp<DepositStackParamList>>();
    const { t } = useTranslation(['kyc', 'wallet']);
    const productName = useActiveProductName();
    const hasSocialWallet = useHasSocialWallet();

    usePreventAndroidBackNavigation();

    const hasInitiatedDepositRef = useRef<boolean>(false);
    const { mutate: deposit } = useDeposit();
    const handleAvailableFundsGuidance = useTrustlyHandleAvailableFundsGuidance();

    const navigateToFailedDeposit = useCallback(
        (errorCode?: string, description?: React.ReactNode) => {
            navigation.navigate('FailedDepositModal', {
                paymentProvider: PaymentMethodTypes.Trustly,
                ...(errorCode && { errorCode }),
                ...(description && { description }),
            });
        },
        [navigation]
    );

    const handleTrustlyStatus = useCallback(
        (trustlyStatus: TrustlyStatus) => {
            if (trustlyStatus === TrustlyStatusValue.SUCCESS) {
                navigation.navigate('SuccessfulDepositModal', {
                    paymentProvider: PaymentMethodTypes.Trustly,
                    title: hasSocialWallet
                        ? t('coin_purchase_successful')
                        : t('trustly_subsequent_deposit_success_title'),
                    description: hasSocialWallet
                        ? t('coin_purchase_successful_description', {
                              betr_gold: coinPackage?.coin_amount,
                              betr_cash: coinPackage?.cash_amount,
                          })
                        : t('trustly_subsequent_deposit_success_description', {
                              amount: selectedAmount,
                              product: productName,
                          }),
                });
            } else {
                let description;
                if (
                    trustlyStatus === TrustlyStatusValue.INSUFFICIENT_FUNDS ||
                    trustlyStatus === TrustlyStatusValue.FRAUD_ANALYSIS ||
                    trustlyStatus === TrustlyStatusValue.INVALID_ACCOUNT
                ) {
                    description = <TrustlyStatusMessage status={trustlyStatus} />;
                }
                navigateToFailedDeposit(`trustly_status_${trustlyStatus}`, description);
            }
        },
        [navigation, selectedAmount, productName, t, hasSocialWallet, coinPackage, navigateToFailedDeposit]
    );

    const { startTimerForCheckStatusApiAsFallback } = useTrustlyStatus({
        onSuccess: handleTrustlyStatus,
        onError: () => navigateToFailedDeposit(),
        onAvailableFundsGuidance: handleAvailableFundsGuidance,
    });

    useEffect(() => {
        if (!hasInitiatedDepositRef.current) {
            hasInitiatedDepositRef.current = true;

            deposit(
                {
                    type: PaymentMethodTypes.Trustly,
                    currency,
                    payment_type_id,
                },
                {
                    onSuccess: response => {
                        startTimerForCheckStatusApiAsFallback(response.extra.user_wallet_id);
                    },
                    onError: error => {
                        navigateToFailedDeposit(getWalletErrorCode(error));
                    },
                }
            );
        }
    }, [
        navigation,
        selectedAmount,
        deposit,
        currency,
        productName,
        t,
        payment_type_id,
        startTimerForCheckStatusApiAsFallback,
        navigateToFailedDeposit,
    ]);

    return (
        <View style={[common.flex, styles.loaderContainer, common.justifyCenter]}>
            <LoadingSpinner />
        </View>
    );
};
